import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { mockAndDevelopment } from 'src/app.config';
import useAuth from '../../../../hooks/useAuth';
import { getErrorMessage } from 'src/utils/errorHelper';

type LoginJWTProps = {
  OnlyDtDomain?: boolean;
};

const LoginJWT: FC<LoginJWTProps> = ({ OnlyDtDomain = false, ...props }) => {
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const usernameValidation = z
    .string()
    .max(255)
    .min(1, 'Se debe ingresar usuario')
    .regex(/^\S+$/, 'No debe contener espacios');

  const usernameSchema = OnlyDtDomain
    ? usernameValidation.regex(
        /@dt\.gob\.cl$/i,
        'Debe ingresar un correo @dt.gob.cl'
      )
    : usernameValidation.regex(
        /^(?!.*@dt\.gob\.cl$).*$/i,
        'No se permiten correos @dt.gob.cl'
      );

  const loginJWTSchema = z.object({
    username: usernameSchema,
    password: z.string().max(255).min(1, 'Se debe ingresar contraseña'),
  });

  type LoginJWTValues = z.infer<typeof loginJWTSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<LoginJWTValues>({
    resolver: zodResolver(loginJWTSchema),
    defaultValues: {
      username: mockAndDevelopment() ? 'demo@devias.io' : '',
      password: mockAndDevelopment() ? 'Password123!' : '',
    },
  });

  const onSubmit = async (values: LoginJWTValues) => {
    setSubmitError(null);
    try {
      await login(values.username, values.password);
    } catch (err) {
      setSubmitError(getErrorMessage(err));
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      {...props}
    >
      <TextField
        {...register('username')}
        autoFocus
        error={Boolean(touchedFields.username && errors.username)}
        fullWidth
        helperText={touchedFields.username ? errors.username?.message : ''}
        label="Usuario"
        margin="normal"
        type="string"
        variant="outlined"
        autoComplete="off"
      />
      <FormControl
        variant="outlined"
        margin="normal"
        fullWidth
      >
        <InputLabel
          error={Boolean(touchedFields.password && errors.password)}
          htmlFor="outlined-adornment-password"
        >
          Contraseña
        </InputLabel>
        <OutlinedInput
          {...register('password')}
          id="outlined-adornment-password"
          error={Boolean(touchedFields.password && errors.password)}
          label="Contraseña"
          type={showPassword ? 'text' : 'password'}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                aria-errormessage="password-error-text"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <FormHelperText
          id="password-error-text"
          error={Boolean(touchedFields.password && errors.password)}
        >
          {touchedFields.password ? errors.password?.message : ''}
        </FormHelperText>
      </FormControl>
      {submitError && (
        <Box sx={{ mt: 3 }}>
          <Alert
            variant="filled"
            severity="error"
          >
            {submitError}
          </Alert>
        </Box>
      )}
      <Box
        sx={{
          mt: 2,
          marginInline: 'auto',
          width: '90%',
        }}
      >
        <Button
          color="primary"
          disabled={isSubmitting}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
        >
          Iniciar Sesión
        </Button>
      </Box>
    </form>
  );
};

export default LoginJWT;
